import { IStackTokens, IToggleStyles, memoizeFunction, mergeStyleSets } from '@fluentui/react';
import { usePanelMobileView } from 'common/components/Panel/usePanelMobileView';
import { useScreen } from 'common/hooks/useScreen';
import { IAppTheme, useAppTheme } from 'common/theme';

const baseline = 8;
export const spacings = {
  xxl: baseline * 5,
  xl: baseline * 4,
  lg: baseline * 3,
  md: baseline * 2,
  sm: baseline * 1.5,
  xs: baseline,
  xxs: baseline / 2,
};

const getStyles = memoizeFunction((theme: IAppTheme, isMobileView: boolean) => {
  return mergeStyleSets({
    panelSettingSection: {
      padding: isMobileView ? 0 : `10px 0 12px 0`,
    },
    summary: { padding: isMobileView ? `0 ${spacings.lg}px` : 0 },
    mobileViewSummaryButtonStack: {
      width: '100%',
    },
    mobileViewSummaryButton: {
      minHeight: `48px !important`,
      borderRadius: '0px !important',
      padding: `${spacings.xs}px ${spacings.md}px !important`,
      backgroundColor: `${theme.palette.white} !important`,
    },
    familyMemberView: {
      ...(isMobileView
        ? {
            borderTop: `1px solid ${theme.appColors?.gray94ToAnchor40}`,
          }
        : {
            padding: `${spacings.md}px ${spacings.lg}px`,
          }),
    },
    elevatedWithRadius: {
      boxShadow: theme.effects.elevation4,
      borderRadius: 8,
    },
    notificationSetting: {
      ...(isMobileView && {
        borderTop: `1px solid ${theme.appColors?.gray94ToAnchor40}`,
      }),
    },
    noPadding: {
      padding: 0,
    },
    familyMemberNotificationSettingSectionText: {
      ...(isMobileView && {
        padding: `0px ${spacings.lg}px`,
      }),
    },
    disabledLabel: {
      color: theme.semanticColors.bodySubtext,
    },
    inlineActionButton: {
      minHeight: '0px !important',
    },
    inlineActionButtonText: {
      color: theme.palette.themeDarkAlt,
    },
    separator: {
      height: 24,
      padding: 0,
    },
    drillDownText: {
      marginRight: 5,
    },
    personaName: {
      lineHeight: 'normal', // added to match style of default persona text
    },
  });
});

export const useNotificationSettingsStyles = () => {
  const { isMobileView } = usePanelMobileView();
  const theme = useAppTheme();
  return getStyles(theme, isMobileView);
};

const getToggleStyles = memoizeFunction((theme: IAppTheme, isMobileView: boolean): Partial<IToggleStyles> => {
  return mergeStyleSets({
    root: { padding: isMobileView ? 0 : '4px 0', margin: 0 },
    label: { ...theme.appFonts?.Body1, padding: 0, marginLeft: 12 },
  });
});

export const useNotificationToggleStyles = () => {
  const { isMobileView } = usePanelMobileView();
  const theme = useAppTheme();
  return getToggleStyles(theme, isMobileView);
};

export const stackTokens = {
  gapXXL: { childrenGap: spacings.xxl } as IStackTokens,
  gapXL: { childrenGap: spacings.xl } as IStackTokens,
  gapLG: { childrenGap: spacings.lg } as IStackTokens,
  gapMD: { childrenGap: spacings.md } as IStackTokens,
  gapSM: { childrenGap: spacings.sm } as IStackTokens,
  gapXS: { childrenGap: spacings.xs } as IStackTokens,
  gapXXS: { childrenGap: spacings.xxs } as IStackTokens,
};

const getNotificationSettingsConfirmationTokens = memoizeFunction((isMobileScreen: boolean) => ({
  wrapper: {
    childrenGap: spacings.md,
    padding: isMobileScreen ? `0 ${spacings.lg}px` : 0,
  },
  footer: {
    childrenGap: spacings.sm,
    padding: `${spacings.md}px 0 0`,
  },
}));

export const useNotificationSettingsConfirmationTokens = () => {
  const { isMobileScreen } = useScreen();
  return getNotificationSettingsConfirmationTokens(isMobileScreen);
};
